import { api } from '@/config/api';
import type { Template } from './templates.service';

export type CampaignTemplateSnapshot = Pick<Template, 'name' | 'language' | 'category' | 'type' | 'headerType' | 'headerText' | 'headerImageUrl' | 'headerVideoUrl' | 'headerDocumentUrl' | 'headerDocumentFilename' | 'headerLocation' | 'body' | 'footer' | 'buttons' | 'cards' | 'variableMap' | 'headerVariableMap'>;

export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
export type RecipientStatus = 'QUEUED' | 'SENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'UNKNOWN' | 'SKIPPED';

export interface ManualPhone { phone: string; countryCode: string; }

export interface Campaign {
  _id: string; campaignUid: string; companyUid: string;
  name: string; description?: string;
  templateUid: string; waTemplateId: string; language: string;
  whatsappAccountUid: string; phoneNumberId: string;
  audienceContactUids: string[]; audienceGroupUids: string[]; audienceManualPhones: ManualPhone[];
  scheduleMode: 'send_now' | 'scheduled'; scheduledAt?: string;
  status: CampaignStatus;
  startedAt?: string; completedAt?: string; cancelledAt?: string; startError?: string;
  queued?: number; sending?: number; unknown?: number; deferred?: number; skipped?: number;
  totalRecipients: number; sent: number; delivered: number; read: number; failed: number;
  headerOverride?: CampaignHeaderOverride;
  templateVariableOverrides?: Record<string, string>;
  templateVariableMappings?: Record<string, string>;
  createdBy: string; updatedBy?: string; createdAt: string; updatedAt: string;
}

export interface ListResponse { items: Campaign[]; total: number; page: number; limit: number; }

export interface Recipient {
  _id: string; recipientUid: string; campaignUid: string; companyUid: string;
  contactUid?: string; phone: string; countryCode: string;
  variables: Record<string, string>;
  messageSnapshot?: CampaignTemplateSnapshot;
  status: RecipientStatus;
  waMessageId?: string;
  sentAt?: string; deliveredAt?: string; readAt?: string; failedAt?: string;
  failureReason?: string; attempts: number;
  createdAt: string;
}

export interface RecipientsResponse { items: Recipient[]; total: number; page: number; limit: number; }

export interface CampaignHeaderOverride {
  imageUrl?: string;
  videoUrl?: string;
  documentUrl?: string;
  documentFilename?: string;
  location?: { latitude?: number; longitude?: number; name?: string; address?: string };
}

export interface CreateCampaignPayload {
  name: string; description?: string;
  templateUid: string; whatsappAccountUid: string;
  audienceContactUids?: string[]; audienceGroupUids?: string[]; audienceManualPhones?: ManualPhone[];
  /** Per-variable override values applied when a contact field is empty. Map var name → value. */
  templateVariableOverrides?: Record<string, string>;
  /** Per-variable source: 'manual' or a contact field key (firstName, city, fullName, ...). */
  templateVariableMappings?: Record<string, string>;
  /** Per-campaign header media override (image/video/document/location). Must match the template's headerType. */
  headerOverride?: CampaignHeaderOverride;
  scheduleMode?: 'send_now' | 'scheduled'; scheduledAt?: string;
}

export interface AudiencePreview {
  contactsCount: number; manualCount: number; total: number; invalidCount?: number;
  samples?: Array<{ phone: string; name?: string; contactUid?: string; variables: Record<string, string> }>;
}

export type AiProviderName = 'openai' | 'claude' | 'qwen' | 'gemini' | 'mistral';

export interface AiCampaignAssistRequest {
  brief: string;
  templateUid: string;
  tone?: 'formal' | 'friendly' | 'promotional' | 'transactional';
  language?: string;
  provider?: AiProviderName;
  model?: string;
}

export interface AiCampaignAssistResponse {
  name: string;
  description?: string;
  variableOverrides: Record<string, string>;
  reasoning?: string;
  creditsCharged?: number;
}

export const campaignsService = {
  list: async (q: { q?: string; status?: string; accountUid?: string; page?: number; limit?: number } = {}): Promise<ListResponse> =>
    (await api.get('/slighter_crm/campaigns', { params: q })).data,
  get: async (uid: string): Promise<Campaign> =>
    (await api.get(`/slighter_crm/campaigns/${uid}`)).data,
  recipients: async (uid: string, page = 1, limit = 50): Promise<RecipientsResponse> =>
    (await api.get(`/slighter_crm/campaigns/${uid}/recipients`, { params: { page, limit } })).data,
  create: async (p: CreateCampaignPayload): Promise<Campaign> =>
    (await api.post('/slighter_crm/campaigns', p)).data,
  update: async (uid: string, p: Partial<CreateCampaignPayload>): Promise<Campaign> =>
    (await api.patch(`/slighter_crm/campaigns/${uid}`, p)).data,
  remove: async (uid: string) => { await api.delete(`/slighter_crm/campaigns/${uid}`); },
  clone: async (uid: string): Promise<Campaign> => (await api.post(`/slighter_crm/campaigns/${uid}/clone`)).data,
  start: async (uid: string): Promise<Campaign> => (await api.post(`/slighter_crm/campaigns/${uid}/start`)).data,
  pause: async (uid: string): Promise<Campaign> => (await api.post(`/slighter_crm/campaigns/${uid}/pause`)).data,
  resume: async (uid: string): Promise<Campaign> => (await api.post(`/slighter_crm/campaigns/${uid}/resume`)).data,
  cancel: async (uid: string): Promise<Campaign> => (await api.post(`/slighter_crm/campaigns/${uid}/cancel`)).data,
  sync: async (uid: string): Promise<Campaign> => (await api.post(`/slighter_crm/campaigns/${uid}/sync`)).data,
  syncAll: async (): Promise<{ updated: number }> => (await api.post('/slighter_crm/campaigns/sync-all')).data,
  previewAudience: async (p: Pick<CreateCampaignPayload, 'audienceContactUids' | 'audienceGroupUids' | 'audienceManualPhones' | 'templateVariableOverrides' | 'templateVariableMappings'>): Promise<AudiencePreview> =>
    (await api.post('/slighter_crm/campaigns/preview-audience', p)).data,
  aiAssist: async (req: AiCampaignAssistRequest): Promise<AiCampaignAssistResponse> =>
    (await api.post('/slighter_crm/campaigns/ai-assist', req)).data,
};
